import { assertNever } from "@suse-o11y/assert-never";

import type { NodeId } from "~/models/dto";

import { TopologySnapshot } from "../../../app/state/topology/TopologySnapshot";
import type { ExploreSubview } from "../../../app/state/view/viewpoint/ViewPointQueryUtils";

/**
 * The `ExploreSubviewSource` is a facade for the `ExploreSubview`.
 * It makes it easier to create a link to a subview, by providing references to a root node.
 * Under the hood, the `ExploreSubviewSource` is converted to a `ExploreSubview` via selecting nodes from the TopologySnapshot.
 */
export type ExploreSubviewSource =
	| { type: "QUERY"; query: string }
	| { type: "COMPONENT"; componentId: number }
	| { type: "GROUP"; snapshot: undefined | TopologySnapshot; groupId: NodeId }
	| { type: "RELATION"; snapshot: undefined | TopologySnapshot; relationId: NodeId }
	| { type: "INDIRECT_RELATION"; snapshot: undefined | TopologySnapshot; indirectRelationId: NodeId }
	| { type: "GROUP_RELATION"; snapshot: undefined | TopologySnapshot; groupRelationId: NodeId };

/**
 * The function converts a `ExploreSubviewSource` to a `ExploreSubview`.
 */
export const extractExploreSubviewFromSource = (source: ExploreSubviewSource): undefined | ExploreSubview => {
	// QUERY does not need a snapshot
	if (source.type === "QUERY") {
		return source.query;
	}

	// COMPONENT does not need a snapshot
	if (source.type === "COMPONENT") {
		return [source.componentId as NodeId];
	}

	switch (source.type) {
		case "GROUP": {
			return source.snapshot?.groups.get(source.groupId)?.components;
		}
		case "RELATION": {
			const relation = source.snapshot?.relations.get(source.relationId);

			return relation && [relation.source, relation.target];
		}
		case "INDIRECT_RELATION": {
			return (
				source.snapshot && TopologySnapshot.getIndirectRelationComponents(source.indirectRelationId, source.snapshot)
			);
		}
		case "GROUP_RELATION": {
			return source.snapshot && TopologySnapshot.getGroupRelationComponents(source.groupRelationId, source.snapshot);
		}
		default: {
			return assertNever(source);
		}
	}
};
